import { useEffect, useState } from "react";
import { Plus, Trash2, Save, CheckCircle2, GripVertical, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { MultiImageUpload } from "@/components/admin/MultiImageUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";
import { RichTextEditor } from "@/components/RichText";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

const num = (v) => Math.max(parseInt(v || "0", 10) || 0, 0);

function DivisionSettings({ ev, set, testid }) {
  const [text, setText] = useState("");
  const value = ev.divisions || [];
  const limits = ev.division_limits || {};
  const fargo = ev.division_fargo_max || {};
  const add = () => {
    const taken = new Set(value.map((v) => v.toLowerCase()));
    const names = [];
    text.split(",").map((s) => s.trim()).forEach((s) => {
      if (s && !taken.has(s.toLowerCase())) { taken.add(s.toLowerCase()); names.push(s); }
    });
    if (names.length) set({ divisions: [...value, ...names] });
    setText("");
  };
  const remove = (d) => {
    const { [d]: _l, ...restLimits } = limits;
    const { [d]: _f, ...restFargo } = fargo;
    set({ divisions: value.filter((x) => x !== d), division_limits: restLimits, division_fargo_max: restFargo });
  };
  const small = "w-20 rounded-md border border-[#E5E7EB] px-2 py-1 text-sm";
  return (
    <div data-testid={testid}>
      <p className="text-sm text-[#555555] mb-1">Divisions <span className="text-xs text-[#888888]">(optional, players pick one when signing up)</span></p>
      {value.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {value.map((d) => (
            <div key={d} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-white border border-[#E5E7EB] px-2.5 py-1.5" data-testid={`${testid}-row-${d}`}>
              <span className="inline-flex items-center rounded-full bg-[#1E63C4]/10 text-[#1E63C4] px-2.5 py-0.5 text-xs font-semibold min-w-[3.5rem] justify-center" data-testid={`${testid}-chip-${d}`}>{d}</span>
              <label className="inline-flex items-center gap-1.5 text-xs text-[#555555]">
                Max players
                <input type="number" min="0" className={small} value={limits[d] || 0} onChange={(e) => set({ division_limits: { ...limits, [d]: num(e.target.value) } })} data-testid={`${testid}-max-${d}`} />
              </label>
              <label className="inline-flex items-center gap-1.5 text-xs text-[#555555]">
                Fargo max
                <input type="number" min="0" className={small} value={fargo[d] || 0} onChange={(e) => set({ division_fargo_max: { ...fargo, [d]: num(e.target.value) } })} data-testid={`${testid}-fargo-${d}`} />
              </label>
              <button type="button" onClick={() => remove(d)} aria-label={`Remove ${d}`} data-testid={`${testid}-remove-${d}`} className="ml-auto text-[#999999] hover:text-[#E11D2A]"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <p className="text-xs text-[#777777]">0 = no limit. Players over the Fargo max see a warning but can still sign up.</p>
        </div>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-[#E5E7EB] px-2 py-1.5 text-sm"
          placeholder="e.g. 600U, 500U, 400U"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
          onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === ",") { ev.preventDefault(); add(); } }}
          data-testid={`${testid}-input`}
        />
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="rounded-md border border-[#1E63C4] text-[#1E63C4] px-3 py-1.5 text-sm font-semibold hover:bg-[#1E63C4] hover:text-white transition-colors">Add</button>
      </div>
    </div>
  );
}

function EventList({ title, items, listKey, onChange, showLink, showResult, showSignup, onComplete, onSave, busy }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const add = () => onChange(listKey, [...items, { id: crypto.randomUUID(), title: "", date: "", location: "", description: "", image: "", link: "", result: "", signup_list: "", signups_enabled: false, max_players: 0, divisions: [], division_limits: {}, division_fargo_max: {}, gallery: [] }]);
  const upd = (i, k, v) => onChange(listKey, items.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const updMany = (i, patch) => onChange(listKey, items.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const rm = (i) => onChange(listKey, items.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    onChange(listKey, arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg">{title}</h3>
        <div className="flex items-center gap-2">
          <button onClick={add} data-testid={`add-${listKey}-btn`} className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-[#c41320]">
            <Plus className="h-4 w-4" /> Add
          </button>
          <button onClick={onSave} disabled={busy} data-testid={`save-${listKey}-btn`} className="inline-flex items-center gap-1 bg-[#1E63C4] text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-[#174e9c] disabled:opacity-60">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {items.length === 0 && <p className="text-sm text-[#555555]">None yet.</p>}
      {items.length > 1 && <p className="text-xs text-[#555555]">Drag the handle to reorder how events appear on the public page.</p>}
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((e, i) => (
          <div
            key={e.id || i}
            onDragOver={(ev) => { ev.preventDefault(); setOverIdx(i); }}
            onDrop={() => onDrop(i)}
            data-testid={`${listKey}-card-${i}`}
            className={`bg-white rounded-lg border p-4 space-y-3 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <div className="flex justify-between items-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#555555]">
                <button
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  data-testid={`${listKey}-drag-${i}`}
                  className="cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
                  aria-label="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                Event {i + 1}
              </span>
              <button onClick={() => rm(i)} className="text-red-500" data-testid={`remove-${listKey}-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <input className={input} placeholder="Title" value={e.title} onChange={(ev) => upd(i, "title", ev.target.value)} />
            <div className="flex gap-2">
              <input className={input} placeholder="Date" value={e.date} onChange={(ev) => upd(i, "date", ev.target.value)} />
              <input className={input} placeholder="Location" value={e.location} onChange={(ev) => upd(i, "location", ev.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#555555] mb-1">Description</label>
              <RichTextEditor key={e.id || i} value={e.description} onChange={(v) => upd(i, "description", v)} testid={`${listKey}-desc-${i}`} placeholder="Description — type or paste formatted text" />
            </div>
            {(!showSignup || !e.signups_enabled) && (
              <textarea className={input} rows={3} placeholder="Player sign-up list (one name per line) — leave blank to hide" value={e.signup_list || ""} onChange={(ev) => upd(i, "signup_list", ev.target.value)} data-testid={`${listKey}-signup-${i}`} />
            )}
            <ImageUpload label="Poster / image" value={e.image} onChange={(v) => upd(i, "image", v)} testid={`${listKey}-image-${i}`} />
            <MultiImageUpload label="Event photos (gallery)" value={e.gallery || []} onChange={(v) => upd(i, "gallery", v)} testid={`${listKey}-gallery-${i}`} />
            {showLink && <input className={input} placeholder="Website link" value={e.link} onChange={(ev) => upd(i, "link", ev.target.value)} />}
            {showResult && <textarea className={input} rows={3} placeholder="Winner / result (line breaks supported)" value={e.result || ""} onChange={(ev) => upd(i, "result", ev.target.value)} data-testid={`${listKey}-result-${i}`} />}
            {showSignup && (
              <div className="space-y-2.5 bg-[#F9FAF8] border border-[#E5E7EB] rounded-md px-3 py-2.5">
                <label className="flex items-center gap-2 text-sm font-medium text-[#333333] cursor-pointer" data-testid={`${listKey}-signups-toggle-${i}`}>
                  <input type="checkbox" checked={!!e.signups_enabled} onChange={(ev) => upd(i, "signups_enabled", ev.target.checked)} className="h-4 w-4 accent-[#E11D2A]" />
                  Enable the Sign Up button for this event
                </label>
                {e.signups_enabled && (
                  <>
                    {(e.divisions || []).length === 0 && (
                      <label className="flex items-center gap-2 text-sm text-[#555555]">
                        Max players (0 = no limit):
                        <input type="number" min="0" className="w-24 rounded-md border border-[#E5E7EB] px-2 py-1.5 text-sm" value={e.max_players || 0} onChange={(ev) => upd(i, "max_players", parseInt(ev.target.value || "0", 10) || 0)} data-testid={`${listKey}-max-${i}`} />
                      </label>
                    )}
                    <DivisionSettings ev={e} set={(patch) => updMany(i, patch)} testid={`${listKey}-divisions-${i}`} />
                    <p className="text-xs text-[#777777]">The player list is built automatically from registrations.</p>
                  </>
                )}
              </div>
            )}
            <ShareToFacebook
              images={[e.image, ...(e.gallery || [])].filter(Boolean)}
              defaultCaption={[e.title, [e.date, e.location].filter(Boolean).join(" • "), e.description].filter(Boolean).join("\n\n")}
              source="tournament"
              testid={`fb-${listKey}-${i}`}
              label={(e.gallery || []).filter(Boolean).length > 0 ? "Share all photos to Facebook" : "Share to Facebook"}
              compact={false}
            />
            <details className="bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5">
              <summary className="text-sm font-semibold text-[#1E63C4] cursor-pointer select-none" data-testid={`${listKey}-seo-toggle-${i}`}>Tournament page — SEO &amp; live settings</summary>
              <div className="mt-3 space-y-2.5">
                {e.slug && (
                  <p className="text-xs text-[#777]">Permanent URL: <a href={`/tournaments/${e.slug}`} target="_blank" rel="noreferrer" className="text-[#1E63C4] hover:underline">/tournaments/{e.slug}</a></p>
                )}
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className={input} placeholder="Venue name (e.g. Stix & Stones)" value={e.venue || ""} onChange={(ev) => upd(i, "venue", ev.target.value)} data-testid={`${listKey}-venue-${i}`} />
                  <input className={input} placeholder="City (e.g. Trenton)" value={e.city || ""} onChange={(ev) => upd(i, "city", ev.target.value)} data-testid={`${listKey}-city-${i}`} />
                  <input className={input} placeholder="Province (e.g. Ontario)" value={e.province || ""} onChange={(ev) => upd(i, "province", ev.target.value)} data-testid={`${listKey}-province-${i}`} />
                  <input className={input} type="datetime-local" placeholder="Start date/time" value={e.start_datetime || ""} onChange={(ev) => upd(i, "start_datetime", ev.target.value)} data-testid={`${listKey}-start-${i}`} />
                </div>
                <input className={input} placeholder="Live tracking ID (Challonge local ID — links live results to this page)" value={e.live_ltid || ""} onChange={(ev) => upd(i, "live_ltid", ev.target.value)} data-testid={`${listKey}-ltid-${i}`} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className={input} placeholder="Champion" value={e.champion || ""} onChange={(ev) => upd(i, "champion", ev.target.value)} data-testid={`${listKey}-champion-${i}`} />
                  <input className={input} placeholder="Runner-up" value={e.runner_up || ""} onChange={(ev) => upd(i, "runner_up", ev.target.value)} data-testid={`${listKey}-runnerup-${i}`} />
                  <input className={input} placeholder="Replay video URL (optional)" value={e.replay_url || ""} onChange={(ev) => upd(i, "replay_url", ev.target.value)} data-testid={`${listKey}-replay-${i}`} />
                  <input className={input} placeholder="Recap link (optional)" value={e.recap_url || ""} onChange={(ev) => upd(i, "recap_url", ev.target.value)} data-testid={`${listKey}-recap-${i}`} />
                </div>
                <input className={input} placeholder="SEO title override (optional)" value={e.seo_title || ""} onChange={(ev) => upd(i, "seo_title", ev.target.value)} data-testid={`${listKey}-seotitle-${i}`} />
                <textarea className={input} rows={2} placeholder="SEO description override (optional, ~155 chars)" value={e.seo_description || ""} onChange={(ev) => upd(i, "seo_description", ev.target.value)} data-testid={`${listKey}-seodesc-${i}`} />
              </div>
            </details>
            {onComplete && (
              <button onClick={() => onComplete(listKey, i)} data-testid={`complete-${listKey}-${i}`} className="w-full inline-flex items-center justify-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-[#174e9c]">
                <CheckCircle2 className="h-4 w-4" /> Mark as completed
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TournamentsManager() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completeModal, setCompleteModal] = useState(null); // { listKey, index }
  const [completeText, setCompleteText] = useState("");

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const onChange = (k, v) => setSite((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Tournaments saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const onComplete = (listKey, index) => {
    const src = site[listKey] || [];
    setCompleteText(src[index]?.result || "");
    setCompleteModal({ listKey, index });
  };

  const confirmComplete = async () => {
    if (!completeModal) return;
    const { listKey, index } = completeModal;
    const src = site[listKey] || [];
    const ev = { ...src[index], result: completeText };
    const newSite = {
      ...site,
      [listKey]: src.filter((_, i) => i !== index),
      previous_results: [ev, ...(site.previous_results || [])],
    };
    setSite(newSite);
    setCompleteModal(null);
    try {
      await api.put("/admin/site", newSite);
      toast.success("Marked completed — moved to Previous Results");
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="tournaments-manager">
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <label className="block text-sm font-medium text-[#333333] mb-1">Intro text</label>
        <textarea className={input} rows={2} value={site.tournaments_intro || ""} onChange={(e) => onChange("tournaments_intro", e.target.value)} />
      </div>

      <EventList title="Upcoming — Local" items={site.upcoming_local || []} listKey="upcoming_local" onChange={onChange} showLink={false} showSignup={true} onComplete={onComplete} onSave={save} busy={busy} />
      <EventList title="Upcoming — National" items={site.upcoming_national || []} listKey="upcoming_national" onChange={onChange} showLink={true} onComplete={onComplete} onSave={save} busy={busy} />
      <EventList title="Previous Results (Local)" items={site.previous_results || []} listKey="previous_results" onChange={onChange} showLink={false} showResult={true} onSave={save} busy={busy} />

      <button onClick={save} disabled={busy} data-testid="save-tournaments-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save tournaments"}
      </button>

      {completeModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setCompleteModal(null)} data-testid="complete-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg mb-1">Winner / result</h3>
            <p className="text-sm text-[#555555] mb-3">Optional. Line breaks are supported — press Enter for a new line. Leave blank to skip.</p>
            <textarea
              autoFocus
              rows={5}
              value={completeText}
              onChange={(ev) => setCompleteText(ev.target.value)}
              data-testid="complete-modal-textarea"
              placeholder={"e.g.\n1st: Jane Doe\n2nd: John Smith\n3rd: Sam Lee"}
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4] resize-y"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setCompleteModal(null)} data-testid="complete-modal-cancel" className="text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]">Cancel</button>
              <button onClick={confirmComplete} data-testid="complete-modal-confirm" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c]">
                <CheckCircle2 className="h-4 w-4" /> Mark completed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
